'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import { PAGE_SIZE } from '../../constants';
import { buildPaginationMeta } from '../../utils';
import { SearchContentsCardSkeletonList } from '../SearchContentsCardSkeleton';
import { SearchSectionShell } from '../SearchSectionShell';
import { PolicyInfoFilter } from './PolicyInfoFilter';
import { PolicyInfoList } from './PolicyInfoList';

interface PolicyInfoSearchProps {
  keyword: string;
  page: number;
  onPageChange: (page: number) => void;
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  provisionCodes: string[];
  onToggleProvision: (code: string) => void;
  onReset: () => void;
}

export function PolicyInfoSearch({
  keyword,
  page,
  onPageChange,
  isFilterOpen,
  onCloseFilter,
  provisionCodes,
  onToggleProvision,
  onReset,
}: PolicyInfoSearchProps) {
  const { data, isLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/best-contents']({
      query: {
        searchKeyword: keyword,
        provisionCodeList:
          provisionCodes.length > 0 ? provisionCodes : undefined,
        bestContentsSort: 'LATEST',
        page,
        size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
  });

  const meta = buildPaginationMeta(data?.context?.totalCount ?? 0);

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      leftAside={
        <PolicyInfoFilter
          isDrawerOpen={isFilterOpen}
          onCloseDrawer={onCloseFilter}
          onApplyDrawer={onCloseFilter}
          provisionCodes={provisionCodes}
          onToggleProvision={onToggleProvision}
          onReset={onReset}
        />
      }
      rightAside={<DesktopSideAdBanner />}
    >
      <SearchSectionShell
        meta={meta}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
      >
        {isLoading ? (
          <SearchContentsCardSkeletonList count={PAGE_SIZE} />
        ) : (
          <PolicyInfoList items={data?.context?.contents ?? []} />
        )}
      </SearchSectionShell>
    </DesktopPageShell>
  );
}
